import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEmpty } from '@semios/app-platform-common'
import { useEffect, useMemo, useState } from 'react'
import { useApiREST } from 'utils/useApiREST'
import { useGroupsAndContacts } from '../../ContactsAndGroups/ContactsAndGroupsContext'
import type { Report } from '../_types'
import type { TReportType } from '../ReportForm/ReportType/ReportType'
import {
  WEEKLY_TRAP_CATCHES_REPORT_TYPE,
  WEEKLY_TRAP_ISSUES_REPORT_TYPE,
  WEEKLY_UPDATE_REPORT_TYPE,
} from '../ReportForm/ReportType/ReportType'
import { convertAPIWeeklyGeneralReportToLocalFormat } from './weeklyGeneralReportAPIHandling/convertAPIWeeklyGeneralReportToLocalFormat'
import { convertAPIWeeklyTrapReportToLocalFormat } from './weeklyTrapCatchesReportAPIHandling/convertAPIWeeklyTrapReportToLocalFormat'
import { convertAPIWeeklyTrapIssuesReportToLocalFormat } from './weeklyTrapIssuesReportAPIHandling/convertAPIWeeklyTrapIssuesReportToLocalFormat'

const requestBody = {
  legacyReportsWeeklyGeneral: {
    attributes: {
      createdAt: true,
      updatedAt: true,
      deletedAt: true,
      reportId: true,
      name: true,
      notifyOwnerByEmail: true,
      contacts: {
        contactId: true,
        email: true,
        language: true,
        name: true,
        sms: true,
      },
      groups: {
        groupId: true,
        name: true,
        contactsForGroup: {
          contactId: true,
          email: true,
          language: true,
          name: true,
          sms: true,
        },
      },
      dayOfWeek: true,
      propertyIds: true,
      chill: true,
      evapotranspiration: true,
      fireBlight: true,
      growingDegreeDays: true,
      insects: true,
      irrigationActivity: true,
      pollination: true,
      soilMoisture: true,
      weather: true,
    },
  },
  legacyReportsWeeklyTrapCatches: {
    attributes: {
      createdAt: true,
      updatedAt: true,
      deletedAt: true,
      reportId: true,
      name: true,
      notifyOwnerByEmail: true,
      contacts: {
        contactId: true,
        email: true,
        language: true,
        name: true,
        sms: true,
      },
      groups: {
        groupId: true,
        name: true,
        contactsForGroup: {
          contactId: true,
          email: true,
          language: true,
          name: true,
          sms: true,
        },
      },
      dayOfWeek: true,
      propertyIds: true,
      rules: true,
      startDate: true,
      endDate: true,
    },
  },
  legacyReportsWeeklyTrapIssues: {
    attributes: {
      createdAt: true,
      updatedAt: true,
      deletedAt: true,
      reportId: true,
      name: true,
      notifyOwnerByEmail: true,
      contacts: {
        contactId: true,
        email: true,
        language: true,
        name: true,
        sms: true,
      },
      groups: {
        groupId: true,
        name: true,
        contactsForGroup: {
          contactId: true,
          email: true,
          language: true,
          name: true,
          sms: true,
        },
      },
      dayOfWeek: true,
      propertyIds: true,
      rules: true,
      startDate: true,
      endDate: true,
    },
  },
} as const

export const useReports = () => {
  const [error, setError] = useState<unknown>(null)
  const [trapCatchReports, setTrapCatchReports] = useState<NonNullable<Report['weeklyTrapCatches']>>([])
  const [trapIssuesReports, setTrapIssuesReports] = useState<NonNullable<Report['weeklyTrapIssues']>>([])
  const [generalReports, setGeneralReports] = useState<NonNullable<Report['weeklyGeneral']>>([])
  const { groups, contacts, loading: contactsAndGroupsLoading } = useGroupsAndContacts()

  const {
    loading,
    refreshHandle,
    error: fetchError,
  } = useApiREST({
    body: requestBody,
    url: '/user-profile',
    initialLoading: true,
    preventFetch: contactsAndGroupsLoading,
    shaper: (response) => {
      if (!isEmpty(response.errors)) {
        setError(response.errors)

        setTrapCatchReports(() => [])

        setTrapIssuesReports(() => [])

        setGeneralReports(() => [])
      }

      const resp = response as routes.UserProfile.Response<typeof requestBody>

      setGeneralReports(
        () =>
          Object.values(resp.data.legacyReportsWeeklyGeneral ?? {})?.map((newReportRaw) =>
            convertAPIWeeklyGeneralReportToLocalFormat(newReportRaw, groups, contacts),
          ) ?? [],
      )

      setTrapCatchReports(
        () =>
          Object.values(resp.data.legacyReportsWeeklyTrapCatches ?? {})?.map((newReportRaw) =>
            convertAPIWeeklyTrapReportToLocalFormat(newReportRaw, groups, contacts),
          ) ?? [],
      )

      setTrapIssuesReports(
        () =>
          Object.values(resp.data.legacyReportsWeeklyTrapIssues ?? {})?.map((newReportRaw) =>
            convertAPIWeeklyTrapIssuesReportToLocalFormat(newReportRaw, groups, contacts),
          ) ?? [],
      )
    },
  })

  useEffect(() => {
    if (fetchError) {
      setError(fetchError)
    }
  }, [fetchError])

  const deleteReportHandler = (reportId: string) => {
    setTrapCatchReports((prev) => prev?.filter((report) => report.id !== reportId))

    setTrapIssuesReports((prev) => prev?.filter((report) => report.id !== reportId))

    setGeneralReports((prev) => prev?.filter((report) => report.id !== reportId))
  }

  const updateReportHandler = <ReportType extends TReportType>(
    report: ReportType extends typeof WEEKLY_TRAP_CATCHES_REPORT_TYPE
      ? NonNullable<
          routes.LegacyWeeklyTrapCatchesReportsManagement.Response['data']['legacyWeeklyTrapCatchesReportsToUpdate']
        >[number]
      : ReportType extends typeof WEEKLY_TRAP_ISSUES_REPORT_TYPE
        ? NonNullable<
            routes.LegacyWeeklyTrapIssuesReportsManagement.Response['data']['legacyWeeklyTrapIssuesReportsToUpdate']
          >[number]
        : NonNullable<
            routes.LegacyWeeklyGeneralReportsManagement.Response['data']['legacyWeeklyGeneralReportsToUpdate']
          >[number],
    reportType: ReportType,
  ) => {
    if (!report || !report.reportId) {
      return
    }

    if (reportType === WEEKLY_UPDATE_REPORT_TYPE) {
      const generalReport = report as NonNullable<
        routes.LegacyWeeklyGeneralReportsManagement.Response['data']['legacyWeeklyGeneralReportsToUpdate']
      >[number]

      setGeneralReports((prev) => {
        return prev.map((r) => {
          if (!r.id) {
            return r
          }

          if (r.id === String(generalReport.reportId)) {
            return convertAPIWeeklyGeneralReportToLocalFormat(generalReport, groups, contacts)
          }

          return r
        })
      })
    }

    if (reportType === WEEKLY_TRAP_CATCHES_REPORT_TYPE) {
      const trapCatchReport = report as NonNullable<
        routes.LegacyWeeklyTrapCatchesReportsManagement.Response['data']['legacyWeeklyTrapCatchesReportsToUpdate']
      >[number]

      setTrapCatchReports((prev) => {
        return prev.map((r) => {
          if (!r.id) {
            return r
          }

          if (r.id !== String(trapCatchReport.reportId)) {
            return r
          }

          return convertAPIWeeklyTrapReportToLocalFormat(trapCatchReport, groups, contacts)
        })
      })
    }

    if (reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE) {
      const trapIssuesReport = report as NonNullable<
        routes.LegacyWeeklyTrapIssuesReportsManagement.Response['data']['legacyWeeklyTrapIssuesReportsToUpdate']
      >[number]

      setTrapIssuesReports((prev) => {
        return prev.map((r) => {
          if (!r.id) {
            return r
          }

          if (r.id !== String(trapIssuesReport.reportId)) {
            return r
          }

          return convertAPIWeeklyTrapIssuesReportToLocalFormat(trapIssuesReport, groups, contacts)
        })
      })
    }
  }

  const createReportHandler = (
    report:
      | NonNullable<
          routes.LegacyWeeklyTrapCatchesReportsManagement.Response['data']['legacyWeeklyTrapCatchesReportsToCreate']
        >[number]
      | NonNullable<
          routes.LegacyWeeklyTrapIssuesReportsManagement.Response['data']['legacyWeeklyTrapIssuesReportsToCreate']
        >[number]
      | NonNullable<
          routes.LegacyWeeklyGeneralReportsManagement.Response['data']['legacyWeeklyGeneralReportsToCreate']
        >[number],
    reportType: TReportType,
  ) => {
    if (reportType === WEEKLY_TRAP_CATCHES_REPORT_TYPE) {
      const newReport = report as NonNullable<
        routes.LegacyWeeklyTrapCatchesReportsManagement.Response['data']['legacyWeeklyTrapCatchesReportsToCreate']
      >[number]

      setTrapCatchReports((prev) => [
        ...prev,
        convertAPIWeeklyTrapReportToLocalFormat(newReport, groups, contacts),
      ])
    } else if (reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE) {
      const newReport = report as NonNullable<
        routes.LegacyWeeklyTrapIssuesReportsManagement.Response['data']['legacyWeeklyTrapIssuesReportsToCreate']
      >[number]

      setTrapIssuesReports((prev) => [
        ...prev,
        convertAPIWeeklyTrapIssuesReportToLocalFormat(newReport, groups, contacts),
      ])
    } else if (reportType === WEEKLY_UPDATE_REPORT_TYPE) {
      const newReport = report as NonNullable<
        routes.LegacyWeeklyGeneralReportsManagement.Response['data']['legacyWeeklyGeneralReportsToCreate']
      >[number]

      setGeneralReports((prev) => [
        ...prev,
        convertAPIWeeklyGeneralReportToLocalFormat(newReport, groups, contacts),
      ])
    }
  }

  const reports = useMemo(
    () => ({
      weeklyTrapCatches: trapCatchReports,
      weeklyTrapIssues: trapIssuesReports,
      weeklyGeneral: generalReports,
    }),
    [trapCatchReports, trapIssuesReports, generalReports],
  )

  return {
    reports,
    loading,
    refreshHandle,
    error,
    deleteReportHandler,
    createReportHandler,
    updateReportHandler,
  }
}
